import { getContext, setContext } from 'svelte';
import { createTileListOnEx } from '@tilelist/application/core/OnExFactory.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageLoadResult } from '@tilelist/application/core/TileListSession.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import type { TileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import {
    type StrategyFactory,
    createTileListBrowserWiring,
} from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import type { WidgetSpinnerClient } from '@tilelist/components/promo-widget-spinner/WidgetSpinnerClient.ts';
import { ProductTileController } from '@tilelist/controllers/ProductTileController.browser.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

export type TileListWiring = {
    onex: OnEx;
    trackingService: TrackingPort | null;
    variationLoadingService: VariationLoader | null;
    widgetSpinnerClient: WidgetSpinnerClient | null;
    controller: ProductTileController | null;
    eventsIncoming: EventsIncoming | null;
    eventsOutgoing: EventsOutgoing | null;
    restoreScrollPosition: () => Promise<void>;
};

export const WIRING_KEY: unique symbol = Symbol('TileListWiring');

/**
 *
 *
 *
 *
 *
 */
export function createAndProvideWiring(initialSession: TileListInitialSession): TileListWiring {
    const onex = createTileListOnEx(initialSession.session);
    const wiring = $state<TileListWiring>({
        onex,
        trackingService: null,
        variationLoadingService: null,
        widgetSpinnerClient: null,
        controller: null,
        eventsIncoming: null,
        eventsOutgoing: null,
        restoreScrollPosition: () => Promise.resolve(),
    });
    setContext(WIRING_KEY, wiring);
    return wiring;
}

/**
 *
 *
 *
 *
 *
 */
export function mountWiring(
    ctx: TileListWiring,
    initialSession: TileListInitialSession,
    onReload: (result: TileListPageLoadResult, browserWiring: TileListBrowserWiring) => void | Promise<void>,
    strategyFactory?: StrategyFactory,
): { browserWiring: TileListBrowserWiring; cleanup: () => void } {
    const browserWiring = createTileListBrowserWiring(initialSession, ctx.onex, strategyFactory);

    ctx.eventsIncoming = browserWiring.eventsIncoming;
    ctx.eventsOutgoing = browserWiring.eventsOutgoing;
    ctx.trackingService = browserWiring.trackingService;
    ctx.variationLoadingService = browserWiring.variationLoadingService;
    ctx.widgetSpinnerClient = browserWiring.widgetSpinnerClient;
    ctx.restoreScrollPosition = browserWiring.restoreScrollPosition;
    ctx.controller = new ProductTileController(
        browserWiring.variationLoadingService,
        browserWiring.trackingService,
        initialSession.session.entryRule,
    );

    const unsub = browserWiring.eventsIncoming.onPageResolved(async (event) => {
        const result = await browserWiring.session.reload(event);
        await onReload(result, browserWiring);
    });

    return { browserWiring, cleanup: () => unsub() };
}

const NULL_WIRING: TileListWiring = {
    /*                                                                             */
    /*                                                                              */
    get onex(): OnEx {
        throw new Error('injectWiring: no wiring context available (onex is unset)');
    },
    trackingService: null,
    variationLoadingService: null,
    widgetSpinnerClient: null,
    controller: null,
    eventsIncoming: null,
    eventsOutgoing: null,
    restoreScrollPosition: () => Promise.resolve(),
};

/*                                                                              */
type RequiredWiring<K extends keyof TileListWiring> = Omit<TileListWiring, K> & {
    [P in K]-?: NonNullable<TileListWiring[P]>;
};

/**
 *
 *
 *
 *
 *
 *
 */
export function injectWiring(): TileListWiring;
export function injectWiring<K extends keyof TileListWiring>(required: readonly K[]): RequiredWiring<K>;
export function injectWiring(_required?: readonly (keyof TileListWiring)[]): TileListWiring {
    /*                                                                                                                             */
    return getContext<TileListWiring>(WIRING_KEY) ?? NULL_WIRING;
}
